import { getAnalytics } from "@tm/insurance/analytics";
import { convertUnixToDate, formatDate } from "@tm/insurance/utils";
import { DATE_FORMATS } from "@tm/isomorphic-utils";
import { BlueChev } from "@tm/static/icons";
import { TextNodeType } from "@tm/ui-widgets/components";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import React, { useCallback, useRef, useState } from "react";
import FormattedText from "../FormattedText/FormattedText";
import {
  AuthorDetails,
  AuthorName,
  AuthorSection,
  CardHeader,
  CardsContainer,
  CarouselButton,
  CarouselContent,
  CarouselWrapper,
  ChevStateWrap,
  DatePosted,
  Dot,
  DotsContainer,
  ProfileIcon,
  Separator,
  Star,
  StarsContainer,
  TestimonialCard,
  TestimonialContent,
  TestimonialsHeader,
  TestimonialsSectionTitle,
  TestimonialsSectionWrapper,
  TooltipWrapper,
} from "./Testimonials.style";

export interface ITestimonialsSection {
  title: TextNodeType;
  cards: {
    header: {
      title: TextNodeType;
      ratings: number;
    };
    content: {
      testimonial: TextNodeType;
      author: TextNodeType;
      datePosted: number;
      profileIcon: string;
    };
  }[];
}

interface TestimonialsSectionProps {
  data: ITestimonialsSection;
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ data }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  // const [value, setValue] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);
  const itemsPerView = 2;
  const maxIndex = Math.max(0, data.cards.length - Math.floor(itemsPerView));

  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackCommonLandingEvents({
        actionType: "testimonials_section_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: testimonialsSectionRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5, // Trigger when 50% of component is visible
      triggerOnLeaveViewport: true,
    }
  );

  // useEffect(() => {
  //   setTimeout(() => {
  //     setValue(true);
  //   }, 1000);
  // }, []);

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
  };

  const isContentTruncated = (element: HTMLDivElement | null): boolean => {
    if (!element) return false;
    return (
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth
    );
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star key={index} filled={index < rating}>
        ★
      </Star>
    ));
  };

  return (
    <TestimonialsSectionWrapper ref={testimonialsSectionRef}>
      <TestimonialsHeader>
        <TestimonialsSectionTitle>
          <FormattedText value={data.title} />
        </TestimonialsSectionTitle>
        <CarouselButton>
          <ChevStateWrap
            left={true}
            onClick={handlePrev}
            disabled={currentIndex === 0}
          >
            <BlueChev />
          </ChevStateWrap>
          <ChevStateWrap
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
          >
            <BlueChev />
          </ChevStateWrap>
        </CarouselButton>
      </TestimonialsHeader>
      <CarouselWrapper>
        <CarouselContent>
          <CardsContainer
            currentIndex={currentIndex}
            itemsPerView={itemsPerView}
          >
            {data.cards.map((card, index) => (
              <TestimonialCard
                key={index}
                onMouseEnter={() => {
                  setHoveredCardIndex(index);
                  if (isContentTruncated(contentRefs.current[index])) {
                    setShowTooltip(true);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredCardIndex(null);
                  setShowTooltip(false);
                }}
              >
                <CardHeader>
                  <FormattedText
                    value={card.header.title}
                    baseStyle={{ fontSize: "14px" }}
                  />
                  <StarsContainer>
                    {renderStars(card.header.ratings)}
                  </StarsContainer>
                </CardHeader>
                <Separator />

                <TestimonialContent
                  ref={(el) => {
                    contentRefs.current[index] = el;
                  }}
                >
                  <FormattedText value={card.content.testimonial} />
                </TestimonialContent>
                {showTooltip && hoveredCardIndex === index && (
                  <TooltipWrapper>
                    <FormattedText value={card.content.testimonial} />
                  </TooltipWrapper>
                )}

                <AuthorSection>
                  <ProfileIcon src={card.content.profileIcon} />
                  <AuthorDetails>
                    <AuthorName>
                      <FormattedText value={card.content.author} />
                    </AuthorName>
                    <DatePosted>
                      {formatDate(
                        convertUnixToDate(card.content.datePosted) ||
                          new Date(),
                        DATE_FORMATS["DD-MMM-YYYY"]
                      ) || ""}
                    </DatePosted>
                  </AuthorDetails>
                </AuthorSection>
              </TestimonialCard>
            ))}
          </CardsContainer>
        </CarouselContent>
      </CarouselWrapper>

      <DotsContainer>
        {Array.from({ length: maxIndex + 1 }, (_, index) => (
          <Dot
            key={index}
            active={index === currentIndex}
            onClick={() => handleDotClick(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </DotsContainer>
    </TestimonialsSectionWrapper>
  );
};

export default TestimonialsSection;
